import {
    createContext,
    useContext,
    useState,
    useEffect,
    useRef,
} from "react";

import {
    collection,
    query,
    where,
    onSnapshot,
} from "firebase/firestore";

import { useAuth }
    from "./AuthContext";

import {
    saveCartToDB,
    subscribeToCart,
} from "../services/cartService";

import { db }
    from "../services/firebase";

import {
    isFutureSlotET
} from "../utils/timeUtils";

const CartContext =
    createContext();

export function useCart() {

    return useContext(
        CartContext
    );
}

export function CartProvider({
    children
}) {

    const {
        currentUser
    } = useAuth();

    const [items, setItems] =
        useState([]);

    /*
     * STORES CONFIRMED HOURS
     * EXAMPLE:
     * {
     *   "2026-05-17": 2,
     *   "2026-05-18": 1
     * }
     */
    const [dailyUsageMap,
        setDailyUsageMap]
        = useState({});

    const [hasLoaded, setHasLoaded] =
        useState(false);

    const unsubscribeRef =
        useRef(null);

    /* ---------------------------------- */
    /* PREVENT SAVE LOOP */
    /* ---------------------------------- */

    const isLocalChange =
        useRef(false);

    /* ---------------------------------- */
    /* VALIDATE CART ITEMS */
    /* ---------------------------------- */

    const filterValidItems = (
        cartItems
    ) => {

        if (
            !Array.isArray(
                cartItems
            )
        ) {

            return [];
        }

        return cartItems.filter(
            (item) => {

                if (!item) {
                    return false;
                }

                if (
                    !item.date ||
                    item.endHour == null
                ) {

                    return false;
                }

                if (
                    Number.isNaN(
                        Number(
                            item.endHour
                        )
                    )
                ) {

                    return false;
                }

                return isFutureSlotET(

                    item.date,

                    Number(
                        item.endHour
                    )
                );
            }
        );
    };

    /* ---------------------------------- */
    /* LOAD FIRESTORE CART */
    /* ---------------------------------- */

    useEffect(() => {

        if (
            unsubscribeRef.current
        ) {

            unsubscribeRef.current();

            unsubscribeRef.current =
                null;
        }

        /* LOGGED OUT */

        if (!currentUser) {

            setItems([]);

            setDailyUsageMap({});

            setHasLoaded(false);

            return;
        }

        setHasLoaded(false);

        const unsubscribe =
            subscribeToCart(

                currentUser.uid,

                async (data) => {

                    const originalItems =
                        data || [];

                    const validItems =
                        filterValidItems(
                            originalItems
                        );

                    /* AUTO CLEAN FIRESTORE */

                    const originalSerialized =
                        JSON.stringify(
                            originalItems
                        );

                    const validSerialized =
                        JSON.stringify(
                            validItems
                        );

                    if (

                        originalSerialized !==
                        validSerialized

                    ) {

                        await saveCartToDB(

                            currentUser.uid,

                            validItems
                        );
                    }

                    setItems(
                        validItems
                    );

                    setHasLoaded(
                        true
                    );
                }
            );

        unsubscribeRef.current =
            unsubscribe;

        return () => {

            if (
                unsubscribe
            ) {

                unsubscribe();
            }
        };

    }, [currentUser]);

    /* ---------------------------------- */
    /* LOAD CONFIRMED DAILY USAGE */
    /* ---------------------------------- */

    useEffect(() => {

        if (!currentUser) {

            setDailyUsageMap({});

            return;
        }

        const usageRef =
            collection(
                db,
                "csc_user_daily_usage"
            );

        const q = query(

            usageRef,

            where(
                "userId",
                "==",
                currentUser.uid
            )
        );

        const unsubscribe =
            onSnapshot(

                q,

                (snapshot) => {

                    const usageMap = {};

                    snapshot.forEach((doc) => {

                        const data =
                            doc.data();

                        usageMap[data.date] =

                            (
                                data.bookedMinutes || 0
                            ) / 60;
                    });

                    setDailyUsageMap(
                        usageMap
                    );
                }
            );

        return () =>
            unsubscribe();

    }, [currentUser]);

    /* ---------------------------------- */
    /* SAVE ONLY LOCAL CHANGES */
    /* ---------------------------------- */

    useEffect(() => {

        if (!currentUser) {
            return;
        }

        if (!hasLoaded) {
            return;
        }

        if (
            !isLocalChange.current
        ) {

            return;
        }

        const timeout =
            setTimeout(

                async () => {

                    try {

                        const validItems =
                            filterValidItems(
                                items
                            );

                        await saveCartToDB(

                            currentUser.uid,

                            validItems
                        );

                    } catch (error) {

                        console.error(

                            "Failed saving cart:",

                            error
                        );

                    } finally {

                        isLocalChange.current =
                            false;
                    }

                },

                300
            );

        return () =>
            clearTimeout(timeout);

    }, [

        items,

        currentUser,

        hasLoaded,
    ]);

    /* ---------------------------------- */
    /* HELPERS */
    /* ---------------------------------- */

    /*
     * ONLY CART HOURS
     */
    const getCartDurationForDate = (
        date
    ) => {

        return items

            .filter(

                (item) =>

                    item.date ===
                    date
            )

            .reduce(

                (sum, item) =>

                    sum +
                    item.duration,

                0
            );
    };

    /*
     * ONLY CONFIRMED HOURS
     */
    const getConfirmedDurationForDate = (
        date
    ) => {

        return (
            dailyUsageMap[date] || 0
        );
    };

    /*
     * TOTAL HOURS
     * CONFIRMED + CART
     */
    const getDurationForDate = (
        date
    ) => {

        const cartHours =
            getCartDurationForDate(
                date
            );

        const bookedHours =
            getConfirmedDurationForDate(
                date
            );

        return (
            cartHours +
            bookedHours
        );
    };

    const getTotalPrice = () => {

        return items.reduce(

            (sum, item) =>

                sum +
                item.price,

            0
        );
    };

    /* ---------------------------------- */
    /* TIME CONFLICT */
    /* ---------------------------------- */

    const hasTimeSlotConflict = (

        date,

        startHour,

        endHour

    ) => {

        return items.some(
            (item) => {

                if (
                    item.date !==
                    date
                ) {

                    return false;
                }

                return (

                    startHour <
                    item.endHour &&

                    endHour >
                    item.startHour
                );
            }
        );
    };

    const getConflictingItem = (

        date,

        startHour,

        endHour

    ) => {

        return items.find(
            (item) => {

                if (
                    item.date !==
                    date
                ) {

                    return false;
                }

                return (

                    startHour <
                    item.endHour &&

                    endHour >
                    item.startHour
                );
            }
        );
    };

    /* ---------------------------------- */
    /* REPLACE ENTIRE CART */
    /* ---------------------------------- */

    const replaceCart = (
        newItems
    ) => {

        isLocalChange.current =
            true;

        setItems(newItems);
    };

    /* ---------------------------------- */
    /* ADD */
    /* ---------------------------------- */

    const addToCart = (
        item
    ) => {

        const validSlot =
            isFutureSlotET(

                item.date,

                item.endHour
            );

        if (!validSlot) {

            return {

                success: false,

                message:
                    "Slot expired.",
            };
        }

        /* DUPLICATE */

        const alreadyExists =
            items.some(

                (cartItem) =>

                    cartItem.slotId ===
                    item.slotId
            );

        if (
            alreadyExists
        ) {

            return {

                success: false,

                message:
                    "Slot already added.",
            };
        }

        /* DAILY LIMIT */

        const currentDuration =
            getDurationForDate(
                item.date
            );

        if (

            currentDuration +
            item.duration >

            2

        ) {

            return {

                success: false,

                message:
                    "Daily booking limit exceeded (2 hours max).",
            };
        }

        /* TIME CONFLICT */

        if (

            hasTimeSlotConflict(

                item.date,

                item.startHour,

                item.endHour
            )

        ) {

            return {

                success: false,

                message:
                    "Time conflict.",
            };
        }

        isLocalChange.current =
            true;

        setItems((prev) => [

            ...prev,

            item,
        ]);

        return {
            success: true,
        };
    };

    /* ---------------------------------- */
    /* REMOVE */
    /* ---------------------------------- */

    const removeFromCart = (
        id
    ) => {

        isLocalChange.current =
            true;

        setItems((prev) =>

            prev.filter(
                (item) =>

                    item.id !== id
            )
        );
    };

    /* ---------------------------------- */
    /* CLEAR */
    /* ---------------------------------- */

    const clearCart = () => {

        isLocalChange.current =
            true;

        setItems([]);
    };

    /* ---------------------------------- */
    /* CONTEXT */
    /* ---------------------------------- */

    return (

        <CartContext.Provider

            value={{

                items,

                dailyUsageMap,

                setItems,

                replaceCart,

                addToCart,

                removeFromCart,

                clearCart,

                getTotalPrice,

                getDurationForDate,

                getCartDurationForDate,

                getConfirmedDurationForDate,

                hasTimeSlotConflict,

                getConflictingItem,
            }}
        >

            {children}

        </CartContext.Provider>
    );
}